import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { getEvents } from '../services/firestoreService';
import { Event as NourirEvent } from '../types';
import { Card, SectionHeader } from './ui';

const formatEventDate = (date: string) => {
  const parsed = parseISO(date);
  if (!isValid(parsed)) return { day: '', month: '', weekday: '', full: date };
  return {
    day: format(parsed, 'd'),
    month: format(parsed, 'MMM', { locale: fr }).replace('.', ''),
    weekday: format(parsed, 'EEEE', { locale: fr }),
    full: format(parsed, 'EEEE d MMMM yyyy', { locale: fr }),
  };
};

const Events = () => {
  const [events, setEvents] = useState<NourirEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsData = await getEvents();
        setEvents(eventsData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Erreur lors du chargement des événements");
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <CalendarIcon className="mx-auto h-12 w-12 text-brand-pink-400 animate-pulse" />
          <p className="mt-4 text-brand-pink-600">Chargement des événements...</p>
        </div>
      );
    }

    if (error) {
      return <p className="text-center py-12 text-red-600">{error}</p>;
    }

    if (events.length === 0) {
      return (
        <Card hover={false} className="max-w-xl mx-auto p-10 text-center">
          <div className="icon-badge h-14 w-14"><CalendarIcon className="h-7 w-7" /></div>
          <h3 className="mt-4 heading text-xl">Prochaines dates bientôt annoncées</h3>
          <p className="mt-2 text-brand-pink-900/75">
            Maraudes, distributions, collectes et événements solidaires : nos prochaines
            actions à Paris et en banlieue sont annoncées en priorité sur Instagram.
          </p>
          <a
            href="https://www.instagram.com/nousrire.asso/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-6"
          >
            Suivre @nousrire.asso
          </a>
        </Card>
      );
    }

    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {events.map((event, index) => {
          const date = formatEventDate(event.date);
          return (
            <motion.article
              key={event.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
            >
              <Card className="h-full flex overflow-hidden">
                {/* Ticket stub */}
                <div className="flex flex-col items-center justify-center px-5 py-6 bg-brand-pink-50 text-accent border-r border-dashed border-ink/15 min-w-[96px]">
                  <span className="text-4xl font-extrabold leading-none">{date.day}</span>
                  <span className="font-semibold uppercase tracking-widest text-xs">{date.month}</span>
                </div>
                <div className="p-6 flex-1 min-w-0">
                  <p className="eyebrow mb-2">{date.weekday}</p>
                  <h3 className="heading text-xl mb-4">
                    {event.title}
                  </h3>
                  <ul className="space-y-2 text-brand-pink-900/75">
                    <li className="flex items-center gap-2">
                      <ClockIcon className="h-5 w-5 text-accent shrink-0" />
                      <span>{event.time}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <MapPinIcon className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                      <span className="line-clamp-2">{event.location}</span>
                    </li>
                  </ul>
                  {event.description && (
                    <p className="mt-4 text-sm text-brand-pink-900/70 leading-relaxed line-clamp-4 whitespace-pre-line">
                      {event.description}
                    </p>
                  )}
                </div>
              </Card>
            </motion.article>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative max-w-7xl mx-auto py-20 md:py-24 px-4 sm:px-6 lg:px-8">
      <SectionHeader
        kicker="Agenda"
        title="Prochains événements"
        lead="Découvrez nos prochains moments de partage et rejoignez-nous pour agir ensemble."
      />
      {renderBody()}
    </div>
  );
};

export default Events;
